// src/components/layout/GlobalModal.tsx
import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { useModalStore } from '../../store/modal-store';
import { useClickOutside } from '../../hooks/useClickOutside';

const GlobalModal: React.FC = () => {
    const { isOpen, title, content, closeModal } = useModalStore();
    const modalRef = useRef<HTMLDivElement | null>(null);

    // Hook for closing the modal when clicking outside
    useClickOutside(modalRef, closeModal);

    // Effect for closing the modal with the Escape key
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeModal();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, closeModal]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                >
                    <motion.div
                        ref={modalRef}
                        initial={{ scale: 0.95, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.95, y: 20, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30, delay: 0.1 }}
                    className="bg-[#1a1d2c] rounded-xl shadow-2xl  max-w-2xl border border-gray-700/80 flex flex-col max-h-[90vh]"
                    >
                        {/* Modal Header */}
                        <header className="flex-shrink-0 flex justify-between items-center px-6 py-4  border-b border-gray-700/50">
                            <h3 className="text-xl font-semibold text-white">{title}</h3>
                            <button onClick={closeModal} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700/50 transition-colors">
                                <FiX size={24} />
                            </button>
                        </header>

                        {/* Modal Content */}
                        <div id="global-modal-content" className="flex-grow px-4 py-4 overflow-y-auto modal-scroll-area  w-100 md:w-150">
                            {content}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default GlobalModal;